import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CircularProgress, IconButton, Tooltip, Typography, Box } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import PauseIcon from '@mui/icons-material/Pause';
import MicOffIcon from '@mui/icons-material/MicOff';
import { authenticatedFetch, readErrorMessage } from '../../utils/authenticatedFetch';

type OpenAIVoiceRecorderProps = {
  onTranscriptionUpdate: (text: string) => void;
  isDisabled?: boolean;
};

type RecorderPhase = 'idle' | 'requesting' | 'recording' | 'transcribing' | 'error';

const MAX_RECORDING_MS = 60_000;

const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/wav',
  'audio/ogg',
];

function pickSupportedMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return undefined;
  }
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
}

export const OpenAIVoiceRecorder: React.FC<OpenAIVoiceRecorderProps> = ({
  onTranscriptionUpdate,
  isDisabled = false,
}) => {
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mimeTypeRef = useRef<string>('audio/webm');
  const stoppedForSubmitRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  const resetRecorder = useCallback(() => {
    clearTimers();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        // ignore
      }
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    releaseStream();
    setElapsedSec(0);
  }, [clearTimers, releaseStream]);

  const transcribeBlob = useCallback(async (blob: Blob) => {
    setPhase('transcribing');
    setError(null);
    try {
      const contentType = (blob.type || mimeTypeRef.current).split(';')[0];
      const response = await authenticatedFetch('/api/transcribe', {
        method: 'POST',
        headers: {
          'Content-Type': contentType,
        },
        body: blob,
      });

      if (!response.ok) {
        const message = await readErrorMessage(response, 'Could not transcribe audio.');
        throw new Error(message);
      }

      const data = (await response.json()) as { text?: string };
      const text = (data.text || '').trim();
      if (text) {
        onTranscriptionUpdate(text);
      } else {
        setError('No speech detected. Please try again.');
      }
      setPhase('idle');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not transcribe audio.');
      setPhase('error');
    }
  }, [onTranscriptionUpdate]);

  const stopRecording = useCallback((submit: boolean) => {
    stoppedForSubmitRef.current = submit;
    clearTimers();
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    } else {
      releaseStream();
      setPhase(submit ? 'idle' : 'idle');
    }
  }, [clearTimers, releaseStream]);

  const startRecording = useCallback(async () => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Voice recording is not supported in this browser.');
      setPhase('error');
      return;
    }

    setPhase('requesting');
    setError(null);
    chunksRef.current = [];
    stoppedForSubmitRef.current = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeType = pickSupportedMimeType();
      mimeTypeRef.current = mimeType || 'audio/webm';
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onerror = () => {
        setError('Recording failed. Please try again.');
        setPhase('error');
        resetRecorder();
      };

      recorder.onstop = async () => {
        clearTimers();
        const shouldSubmit = stoppedForSubmitRef.current;
        const blobType = recorder.mimeType || mimeTypeRef.current;
        const blob = new Blob(chunksRef.current, { type: blobType });
        chunksRef.current = [];
        releaseStream();
        mediaRecorderRef.current = null;

        if (!shouldSubmit) {
          setPhase('idle');
          setElapsedSec(0);
          return;
        }

        if (!blob.size) {
          setError('No audio captured. Please try again.');
          setPhase('error');
          return;
        }

        await transcribeBlob(blob);
        setElapsedSec(0);
      };

      recorder.start();
      setPhase('recording');
      setElapsedSec(0);

      tickRef.current = setInterval(() => {
        setElapsedSec((sec) => sec + 1);
      }, 1000);

      timeoutRef.current = setTimeout(() => {
        stopRecording(true);
      }, MAX_RECORDING_MS);
    } catch (err) {
      releaseStream();
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        setError('Microphone permission denied.');
      } else {
        setError('Could not access the microphone.');
      }
      setPhase('error');
    }
  }, [clearTimers, releaseStream, resetRecorder, stopRecording, transcribeBlob]);

  const toggleRecording = useCallback(async () => {
    if (isDisabled || phase === 'transcribing' || phase === 'requesting') return;
    if (phase === 'recording') {
      stopRecording(true);
      return;
    }
    await startRecording();
  }, [isDisabled, phase, startRecording, stopRecording]);

  useEffect(() => {
    return () => {
      stoppedForSubmitRef.current = false;
      resetRecorder();
    };
  }, [resetRecorder]);

  const busy = phase === 'requesting' || phase === 'transcribing';
  const recording = phase === 'recording';

  let icon = <MicIcon />;
  let label = 'Start voice input';
  if (isDisabled) {
    icon = <MicOffIcon />;
    label = 'Voice input disabled';
  } else if (busy) {
    icon = <CircularProgress size={22} color="inherit" />;
    label = phase === 'transcribing' ? 'Transcribing' : 'Requesting microphone';
  } else if (recording) {
    icon = <PauseIcon />;
    label = 'Stop and transcribe';
  } else if (phase === 'error') {
    label = error || 'Retry voice input';
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={label}>
        <span>
          <IconButton
            type="button"
            onClick={toggleRecording}
            disabled={isDisabled || busy}
            aria-label={label}
            className="speech-to-text-button"
            sx={{
              width: 48, height: 48, borderRadius: '50%', p: 0, border: 'none',
              bgcolor: recording ? 'error.main' : 'primary.main',
              color: 'common.white',
              '&:hover': { bgcolor: recording ? 'error.dark' : 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
            }}
          >
            {icon}
          </IconButton>
        </span>
      </Tooltip>
      {recording && (
        <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 28 }}>
          {elapsedSec}s
        </Typography>
      )}
      {phase === 'transcribing' && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Transcribing…
        </Typography>
      )}
      {error && phase === 'error' && (
        <Typography variant="caption" sx={{ color: 'error.main', maxWidth: 160 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default OpenAIVoiceRecorder;
